import { Link, createFileRoute } from '@tanstack/react-router'
import { Banknote, Building2, Link2, Users } from 'lucide-react'
import { PlaidCostCard } from './-components/PlaidCostCard'
import { PlaidItemsTable } from './-components/PlaidItemsTable'
import { PlaidRatesCard } from './-components/PlaidRatesCard'
import { PlaidRecipientsCard } from './-components/PlaidRecipientsCard'
import { PageTitle } from '@/components/PageTitle'
import { BrandLogo } from '@/components/shared/BrandLogo'
import { BackofficeStatCard } from '@/routes/_app/backoffice/-components/BackofficeStatCard'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { formatDateTime } from '@/lib/formatters'
import { usePlaidStats } from '@/hooks/repositories/usePlaidRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

export const Route = createFileRoute('/_app/backoffice/integrations/plaid/')({
  component: RouteComponent,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.plaid),
})

function RouteComponent() {
  const { data, isLoading } = usePlaidStats()

  const errored = data?.items.errored ?? 0
  // Clients Plaid still charges for despite having no live link — the gap the
  // billable-user tracking exists to surface.
  const stillBillingUnlinked = Math.max(
    0,
    (data?.billableUsers ?? 0) - (data?.clientsLinked ?? 0),
  )
  const itemsStatus =
    errored > 0
      ? { variant: 'error' as const, label: `${errored} needs attention` }
      : (data?.items.total ?? 0) > 0
        ? { variant: 'healthy' as const, label: 'All items active' }
        : { variant: 'idle' as const, label: 'No items linked' }

  return (
    <div className="py-4">
      <PageTitle title="Plaid Integration" />

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice">Backoffice</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice/integrations">Integrations</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Plaid</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-6 flex items-center gap-3">
        <BrandLogo
          domain="plaid.com"
          name="Plaid"
          className="size-8 rounded-xl"
        />
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Plaid
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Account aggregation health and spend. Plaid bills per live Item per
            month, so linked Items you no longer need are recurring cost.
          </p>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <BackofficeStatCard
          accentTile="bg-foreground/5 text-foreground"
          Icon={Link2}
          title="Linked Items"
          routeHint="plaid_items"
          primaryValue={data?.items.total ?? 0}
          primaryLabel="institution connections"
          secondaryLine={`${data?.items.active ?? 0} active · ${errored} errored`}
          status={itemsStatus}
          isLoading={isLoading}
        />
        <BackofficeStatCard
          accentTile="bg-sky-500/10 text-sky-600 dark:text-sky-400"
          Icon={Banknote}
          title="Linked Accounts"
          routeHint="plaid_accounts"
          primaryValue={data?.accounts.total ?? 0}
          primaryLabel="accounts across all Items"
          secondaryLine={
            Object.entries(data?.accounts.byType ?? {})
              .map(([type, count]) => `${count} ${type}`)
              .join(' · ') || undefined
          }
          status={{ variant: 'idle', label: 'Read-only' }}
          isLoading={isLoading}
        />
        <BackofficeStatCard
          accentTile="bg-violet-500/10 text-violet-600 dark:text-violet-400"
          Icon={Building2}
          title="Institutions"
          routeHint="distinct institution_id"
          primaryValue={data?.institutions ?? 0}
          primaryLabel="distinct institutions"
          secondaryLine={
            data?.lastSyncedAt
              ? `Last sync ${formatDateTime(data.lastSyncedAt)}`
              : 'Never synced'
          }
          status={{ variant: 'idle', label: 'Read-only' }}
          isLoading={isLoading}
        />
        <BackofficeStatCard
          accentTile="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          Icon={Users}
          title="Billable Users"
          routeHint="plaid_billable_users"
          primaryValue={data?.billableUsers ?? 0}
          primaryLabel="charged this term"
          secondaryLine={
            stillBillingUnlinked > 0
              ? `${data?.clientsLinked ?? 0} linked · ${stillBillingUnlinked} unlinked, still billing`
              : `${data?.clientsLinked ?? 0} clients linked`
          }
          status={
            stillBillingUnlinked > 0
              ? { variant: 'action', label: 'Paying for unlinked' }
              : { variant: 'idle', label: 'Read-only' }
          }
          isLoading={isLoading}
        />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <PlaidCostCard
          isLoading={isLoading}
          cost={data?.cost}
          term={data?.billingTerm}
        />
        <div className="h-full lg:col-span-2">
          <PlaidRatesCard />
        </div>
      </div>

      <div className="mb-6">
        <PlaidRecipientsCard />
      </div>

      <div className="mb-3">
        <h2 className="text-lg font-semibold tracking-tight">Linked Items</h2>
        <p className="text-sm text-muted-foreground">
          Every Plaid Item across all clients, with the reason behind any error.
        </p>
      </div>
      <PlaidItemsTable />
    </div>
  )
}
